import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Floating chat widget rendered on the public landing page.
 * Talks to /api/public/agents/{agentId}/chat (unauth) which uses the
 * agent configured in app_settings.public_agent_id.
 *
 * Renders NOTHING if no public agent is configured.
 */
export default function PublicChatWidget({ agent }) {
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [convId, setConvId] = useState(null);
  const scrollerRef = useRef(null);

  // Initial greeting once the panel opens
  useEffect(() => {
    if (open && messages.length === 0 && agent?.greeting) {
      setMessages([{ role: "assistant", content: agent.greeting }]);
    }
    if (open) setPulse(false);
  }, [open, agent, messages.length]);

  // Auto scroll
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || busy || !agent?.id) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${BACKEND}/api/public/agents/${agent.id}/chat`,
        { message: text, conversation_id: convId }
      );
      if (data.conversation_id) {
        setConvId(data.conversation_id);
        try { window.localStorage.setItem("co_widget_conv", data.conversation_id); } catch { /* noop */ }
      }
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Sorry — I hit a snag. Try again in a moment, or use the form below to book a demo." },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const quick = [
    "How does pricing work?",
    "What industries do you support?",
    "Book me a demo",
  ];

  if (!agent?.id) return null;

  return (
    <>
      {/* Floating bubble button */}
      {!open && (
        <button
          data-testid="public-chat-fab"
          onClick={() => setOpen(true)}
          className="co-chat-fab"
        >
          <span className="co-chat-fab-ico">
            <MessageCircle className="w-6 h-6" />
          </span>
          <div className="co-chat-fab-copy">
            <div className="co-chat-fab-title">Chat with our AI</div>
            <div className="co-chat-fab-sub">Same tech we install for you</div>
          </div>
          {pulse && <span className="co-chat-fab-pulse" />}
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="co-chat-panel" data-testid="public-chat-panel">
          <div className="co-chat-header">
            <div className="co-chat-header-brand">
              <span className="co-chat-dot" />
              <div>
                <div className="co-chat-name">{agent.name || "ClientsOnramp AI"}</div>
                <div className="co-chat-status">
                  <span className="co-chat-status-dot" />
                  Online now
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="co-chat-close"
              aria-label="Close chat"
              data-testid="public-chat-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="co-chat-hint">
            <Sparkles className="w-3 h-3" />
            This is the actual AI agent we install for your business — try it.
          </div>

          <div ref={scrollerRef} className="co-chat-scroll">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`co-chat-msg ${
                  m.role === "user" ? "co-chat-msg-user" : "co-chat-msg-ai"
                }`}
              >
                {m.content}
              </div>
            ))}
            {busy && (
              <div className="co-chat-msg co-chat-msg-ai co-chat-typing">
                <span /><span /><span />
              </div>
            )}
          </div>

          {messages.length <= 1 && !busy && (
            <div className="co-chat-quicks">
              {quick.map((q) => (
                <button
                  key={q}
                  onClick={() => {
                    setInput(q);
                    setTimeout(() => document.getElementById("co-chat-input")?.focus(), 10);
                  }}
                  className="co-chat-quick"
                  data-testid={`public-chat-quick-${q.slice(0, 8).replace(/\s+/g, '-')}`}
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={send} className="co-chat-input-row">
            <input
              id="co-chat-input"
              data-testid="public-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about pricing, timing, industries…"
              className="co-chat-input"
              disabled={busy}
              autoFocus
            />
            <button
              data-testid="public-chat-send"
              type="submit"
              disabled={busy || !input.trim()}
              className="co-chat-send"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
          <div className="co-chat-fine">
            Powered by ClientsOnramp AI · Not a recording. Real conversation.
          </div>
        </div>
      )}
    </>
  );
}
